import React from 'react';
import { Navigate } from 'react-router-dom';
import { isEqual } from 'lodash-es';

import PageLoader from '@/components/page-loader';
import { OWNERS_HUB_ROUTES } from '@/constants/routes';
import { useAppDispatch, useAppSelector } from '@/store';
import { sessionCheck, sessionLoad } from '@/store/slices/session';
import { userBrandsList } from '@/store/slices/casino-instance';
import { fetchUserPreferences } from '@/store/slices/userPreferences';
import { resolvePostAuthRoute } from '@/utils/post-auth-route';
import { resolveConfiguratorFlowBase } from '@/utils/configurator-flow';

/**
 * Brand-state guard for the app root (`/`).
 *
 * Root was previously config-driven with no code-side guard: it landed wherever
 * the remote config's `/` page pointed, regardless of session or brand. This
 * component is mounted as a fixed React route ahead of the config routes
 * (`routes.tsx`), so a bare `/` load is resolved here:
 *
 *  - no valid session            → `/register`
 *  - session, mid-setup (paying / customising, not yet live) → the deploy wizard,
 *    or `/account/home` (with its Finish setting up notice) when another brand is live
 *  - session, ≥1 deployed brand  → `/account/home`
 *  - session, no brand yet       → `/easy-setup` (the configurator flow every owner goes through)
 *
 * The destination is computed by the shared {@link resolvePostAuthRoute} so the
 * root, the login module and the register module can never drift apart again.
 */
export const RootRedirect = (): React.ReactElement => {
  const dispatch = useAppDispatch();

  const authenticated = useAppSelector((state) => state.authentication.auth_type === 'user');
  const authInProgress = useAppSelector((state) => state.authentication.in_progress);

  const session = useAppSelector(
    (state) => ({
      status: state.session.status,
      loaded: state.session.loaded,
      loading: state.session.loading,
      error: state.session.error,
    }),
    isEqual,
  );
  const casino = useAppSelector(
    (state) => ({
      loaded: state.casinoInstance.loaded,
      loading: state.casinoInstance.loading,
      count: state.casinoInstance.brands.length,
      error: state.casinoInstance.error,
    }),
    isEqual,
  );

  // Kick off the two fetches the routing decision needs, once, after we know the
  // user is authenticated (both calls require a valid token).
  const dispatchedRef = React.useRef(false);
  React.useEffect(() => {
    if (authenticated && !dispatchedRef.current) {
      dispatchedRef.current = true;
      // `ambient: true` — this read only feeds the routing decision; it must not adopt a
      // dead orphan (a stuck `payment_successful`) and route the owner into an onboarding
      // wizard whose first /save 500s. A non-resumable session is dropped, so the owner
      // lands on Home (or a fresh configurator) instead.
      dispatch(sessionLoad({ ambient: true }));
      dispatch(userBrandsList());
      // Probe for a stale, unpaid configurator session at the earliest authenticated
      // point so the home "Spotlight" banner can offer to resume (or cancel) it on
      // first paint. `/check` is idempotent (doesn't mint a session), unlike `/load`.
      dispatch(sessionCheck());
      // Preload the owner's saved preferences at the earliest authenticated point
      // so the brand reconciliation listener (store/index.ts) and Settings have
      // them ready. MainLayout re-guards this for direct (non-root) page loads.
      dispatch(fetchUserPreferences());
    }
  }, [authenticated, dispatch]);

  // Auth is still being verified at boot (the refresh + check pass) — hold rather
  // than bounce a logged-in user to /register on a hard refresh.
  if (authInProgress) {
    return <PageLoader />;
  }

  // No valid session → registration funnel.
  if (!authenticated) {
    return <Navigate to={OWNERS_HUB_ROUTES.REGISTER} replace />;
  }

  // The brand fetch was rejected (e.g. a token that expired server-side but was
  // still present). The 401 interceptor normally redirects first; this is the
  // belt-and-suspenders for any non-401 failure so we never fall through to a
  // misleading "no brands → configurator" decision on a broken list.
  if (casino.error) {
    return <Navigate to={OWNERS_HUB_ROUTES.REGISTER} replace />;
  }

  // Wait for both the configurator session and the brand list to settle
  // (resolved = loaded or errored, and not in flight) before deciding.
  const sessionSettled = (session.loaded || session.error != null) && !session.loading;
  const casinoSettled = (casino.loaded || casino.error != null) && !casino.loading;
  if (!sessionSettled || !casinoSettled) {
    return <PageLoader />;
  }

  const destination = resolvePostAuthRoute({
    sessionStatus: session.error != null ? null : session.status,
    brandsLoaded: casino.loaded,
    brandsCount: casino.count,
    configuratorBase: resolveConfiguratorFlowBase(),
  });
  return <Navigate to={destination} replace />;
};

export default RootRedirect;
